import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ToastContainer, toast } from "react-toastify";
import { useLocation } from "react-router-dom";

import { getIndustryAnalysisData } from "@/services/businessDirectory";
import { dnbComLog, isNotUndefined, LogSeverity } from "@/shared/FunctionCore";
import { industryAnalysisResponseData } from "@/app/pages/industryAnalysis/ConfigAndInterface";

import Breadcrumb from "@/components/breadCrumb";
import Analysis from "@/app/pages/industryAnalysis/Analysis";
import Segmentation from "@/app/pages/industryAnalysis/Segmentation";
import IndustryCompany from "@/app/pages/industryAnalysis/IndustryCompany";
import MoversAndShakers from "@/app/pages/industryAnalysis/MoversAndShakers";
import IndustryCountry from "@/app/pages/industryAnalysis/IndustryCountry";
import Loader from "@/components/loader";
import DandBHoovers from "@/components/banners/DandBhoovers";
import HeadingBlue from "@/components/banners/HeadingBlue";
import {setDigitalData} from "@/services/setDigitalData";
import Overlay from "@/components/overlay";
import { usePageURL } from "@/shared/customHooks";

const IndustryAnalysis = () => {
  const { countryCode } = usePageURL();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const [industryData, setIndustryData] =
    useState<industryAnalysisResponseData>();
  const [isLoading, setIsLoading] = useState(false);
  setDigitalData({gcpPageName: 'industryAnalysisPage', pageTitle: ''})
  const goToTop = useRef<HTMLDivElement>(null);

  const breadcrumbs = [
    { name: t("homeLabel"), url: t("homeRedirection") },
    { name: t("business_directory"), url: t("bdLandingPageRedirectionLink") },
    industryData?.naics2_title && {name: industryData?.naics2_title, url: `/business-directory/industry-analysis.${industryData?.naics2_selector}.html`},
    industryData?.naics3_title && {name: industryData?.naics3_title, url: `/business-directory/industry-analysis.${industryData?.naics3_selector}.html`},
    industryData?.title &&{ name : industryData?.title},
  ];

  const filteredBreadcrumbs = breadcrumbs.filter(Boolean);

  const hooverData = {};
  hooverData['heading'] =  t("industryAnalysis.dAndBHoovers.heading");
  hooverData['content'] = t("industryAnalysis.dAndBHoovers.content");
  hooverData['buttonLabel']  = t("industryAnalysis.dAndBHoovers.buttonLabel");
  hooverData['buttonRedirectionLink'] = t("industryAnalysis.dAndBHoovers.buttonRedirectionLink");

  const heading = {};
  heading['title'] = industryData?.title;
  heading['titleSuffix'] = t("industryAnalysis.BannerHeader.headingSuffix"); 
  heading["titlePrefix"] = t("industryAnalysis.BannerHeader.headingPrefix")
  heading['isTintClassName'] = true;
  const fetchData = () => {
    setIsLoading(true);
    try {
      const splittedPath = pathname.split("/");
      let industryPage = "";
      if (isNotUndefined(splittedPath[2])) {
        industryPage = splittedPath[2].split('.')[1];
      }

      getIndustryAnalysisData(industryPage)
        .then((res) => {
          if (res && res.data !== null) {
            setIndustryData(res.data);
            setDigitalData({gcpPageName: 'industryAnalysisPage', pageTitle: res.data?.title})
            setIsLoading(false);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          dnbComLog(LogSeverity.ERROR, error.message);
        });
    } catch {
      setIsLoading(false);
      dnbComLog(LogSeverity.ERROR, "Something went wrong");
    }
  };

  useEffect(()=>{
    window.scrollTo(0,0); 
  },[])

  useEffect(() => {
    if (pathname) {
      fetchData();
    }
  }, [pathname]);

  return (
    <>
      <ToastContainer position="top-center" />
      {isLoading ? (
        <Loader inProgress={isLoading} />
      ) : industryData ? (
        <>
          <HeadingBlue props={heading} />
          <div className="breadcrumbContainer" ref={goToTop}>
            <Breadcrumb
              breadcrumbs={filteredBreadcrumbs}
              forComponent="businessDirectory"
              delimiter=">"
              styling="font-style-light"
            />
          </div>
          <Analysis
            description={industryData.description}
            activities={industryData.activities}
          />
          <Segmentation
            title={industryData.title}
            chartDataValue={industryData.geographicSegmentation}
          />
          <IndustryCountry
            title={industryData.title}
            countryCrawl={industryData.countryCrawl}
            urlSelector={industryData.urlSelector}
          />
          <IndustryCompany
            title={industryData.title}
            topCompaniesData={industryData.topCompanies}
          />
          <MoversAndShakers
            moversAndShakersData={industryData["moversAndShakers"]}
          />
           <DandBHoovers props={hooverData} />
        </>
      ) : (
        <></>
      )}
       <Overlay
        countryCode={countryCode}
        isEmailDisplay={['pl', 'us'].includes(countryCode)}
        isPhoneDisplay={['de', 'at', 'ch','hu','us'].includes(countryCode)}
        isRecaptchaDisplay={['de','at','ch','hu','pl','us'].includes(countryCode)}
        recaptchaActionName={t("businessDirectory.recaptchActionName")}
      />
    </>
  );
};

export default IndustryAnalysis;
